import {FC, useState} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {Button} from '@components/ui/button'
import {Copy01Icon} from '@components/ui/icon'
import {Assignment} from '@features/assignment/types.ts'
import {ThreeDScanLaunchModal} from '@features/assignment/components/three-d-scan-launch-modal'
import {useMediaQuery} from '@hooks/use-media-query'

const buildScanUrl = (listingId: string) => `airbnb://floorplan-capture/${listingId}`

const buildFloorPlanFormUrl = (listingExternalId: string) => {
    const id = encodeURIComponent(listingExternalId)
    return `https://airtable.com/appRXCOlpwNYOnL6x/pag1n6wbCFU3rE4yr/form?prefill_listing_id=${id}&prefill_listing_id_vi=${id}&hide_listing_id=true`
}

const SCANNING_GUIDELINES_URL = 'https://airbnb.app.box.com/s/bfntfidlm3cnh1gklww6obxxlaqwedp2'

type ThreeDScanCardVariant = 'pending_assets' | 'scheduled'

export const ThreeDScanCard: FC<{assignment: Assignment; variant?: ThreeDScanCardVariant}> = ({
    assignment,
    variant = 'pending_assets'
}) => {
    const {t} = useTranslation()
    const isLarge = useMediaQuery({breakpoint: 'md'})
    const [isModalOpen, setIsModalOpen] = useState(false)
    const scanUrl = buildScanUrl(assignment.entity.external_id)
    const floorPlanFormUrl = buildFloorPlanFormUrl(assignment.entity.external_id)

    return (
        <>
            <div className="flex flex-col gap-2 p-6 bg-white border border-neutral-300 rounded-xl md:rounded-2xl w-full">
                <h3 className="text-lg font-medium text-neutral-900">
                    {variant == 'scheduled' ? t('assignment:three_d_scan:scheduled_title') : t('floor_plan:title')}
                </h3>
                <div className="flex gap-6 w-full flex-col items-stretch md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-3 w-full">
                        {variant == 'scheduled' ? (
                            <>
                                <p className="text-sm text-neutral-900 [&_a]:text-purple-700 [&_a]:font-medium [&_a]:underline [&_a]:cursor-pointer">
                                    {t('assignment:three_d_scan:scheduled_description')}
                                </p>
                                <p className="text-sm text-neutral-900 [&_a]:text-purple-700 [&_a]:font-medium [&_a]:underline [&_a]:cursor-pointer">
                                    <Trans
                                        i18nKey={'floor_plan:guidelines'}
                                        components={{
                                            guidelines: (
                                                <a
                                                    href={SCANNING_GUIDELINES_URL}
                                                    target={'_blank'}
                                                    rel={'noopener noreferrer'}
                                                />
                                            )
                                        }}
                                    />
                                </p>
                            </>
                        ) : (
                            <p className="text-sm text-neutral-900 [&_a]:text-purple-700 [&_a]:font-medium [&_a]:underline [&_a]:cursor-pointer">
                                <Trans
                                    i18nKey={'assignment:three_d_scan:card_description'}
                                    components={{
                                        1: <a href={floorPlanFormUrl} target={'_blank'} rel={'noopener noreferrer'} />
                                    }}
                                />
                            </p>
                        )}
                    </div>

                    {!isLarge ? (
                        <Button className="w-full" onClick={() => setIsModalOpen(true)}>
                            {t('assignment:three_d_scan:launch_cta')}
                        </Button>
                    ) : (
                        <div className="flex flex-col gap-1 min-w-0 max-w-none w-full md:min-w-[420px] md:max-w-[664px]">
                            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 items-stretch w-full">
                                <input
                                    type={'text'}
                                    value={scanUrl}
                                    readOnly
                                    className="w-full py-2.5 px-3.5 border border-neutral-300 rounded-lg bg-white text-neutral-700 font-[inherit] outline-none focus:border-primary-600"
                                />
                                <Button variant={'secondary'} onClick={() => setIsModalOpen(true)}>
                                    <Copy01Icon className="text-neutral-700" />
                                    {t('assignment:three_d_scan:copy')}
                                </Button>
                            </div>
                            <p className="text-sm text-neutral-500">{t('assignment:three_d_scan:link_caption')}</p>
                        </div>
                    )}
                </div>
            </div>

            {isModalOpen && (
                <ThreeDScanLaunchModal
                    assignment={assignment}
                    scanUrl={scanUrl}
                    isMobile={!isLarge}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </>
    )
}
